// Settings → Account → Password: set, change or remove the signed-in person's own password.
// Server side: dashboard/src/passwords.ts (/api/account, /api/account/password).

interface Account {
  available: boolean;
  password: boolean;
  password_set_at: number | null;
  /** Changing or removing needs the current password (false right after an email-link sign-in). */
  needs_current: boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const ERRORS: Record<string, string> = {
  too_short: "Use at least 10 characters.",
  too_long: "Use at most 200 characters.",
  same_as_email: "Your password can't be your email address.",
  common_password: "That password is too common. Pick something less guessable.",
  pwned_password: "That password has appeared in a data breach. Pick a different one.",
  current_required: "Enter your current password.",
  wrong_password: "Your current password is incorrect.",
  too_many_attempts: "Too many attempts. Try again in 15 minutes, or sign in with an email link.",
  no_password: "You don't have a password set.",
  logins_disabled: "Email sign-in isn't switched on for this dashboard.",
  no_account: "Passwords are for people who sign in with their email.",
};

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const day = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

type View = "idle" | "set" | "change" | "remove";

/** Renders the Password section into `box`. `email` fills the hidden username field for password managers. */
export async function renderAccountPassword(box: HTMLElement, email: string, current: () => boolean): Promise<void> {
  let acct: Account;
  try {
    acct = await call<Account>("GET", "account");
  } catch {
    if (current()) box.innerHTML = "";
    return;
  }
  if (!current()) return;
  if (!acct.available) {
    box.innerHTML = "";
    return;
  }
  draw(box, email, acct, "idle", "");
}

function draw(box: HTMLElement, email: string, acct: Account, view: View, notice: string): void {
  const status = acct.password
    ? `You can sign in with your password or an email link. Password set ${acct.password_set_at ? esc(day(acct.password_set_at)) : ""}.`
    : "You sign in with an email link. Add a password to sign in without waiting for an email.";
  const needCurrent = acct.password && acct.needs_current && view !== "set";
  const actions = acct.password
    ? `<button class="btn" type="button" data-view="change">Change password</button><button class="btn" type="button" data-view="remove">Remove password</button>`
    : `<button class="btn" type="button" data-view="set">Set a password</button>`;
  const form =
    view === "idle"
      ? ""
      : `<form class="acct-pw-form" novalidate>
    <input type="text" name="username" autocomplete="username" value="${esc(email)}" hidden readonly>
    ${
      needCurrent
        ? `<label class="fld"><span>Current password</span><div class="pw-wrap"><input class="acct-input" type="password" name="current" autocomplete="current-password" maxlength="200" required aria-describedby="pw-msg"></div></label>`
        : acct.password
          ? `<p class="muted acct-pw-note">You signed in with an email link just now, so your current password isn't needed.</p>`
          : ""
    }
    ${
      view === "remove"
        ? `<p class="muted acct-pw-note">You'll sign in with an email link only. You can add a password again at any time.</p>`
        : `<label class="fld"><span>New password</span><div class="pw-wrap"><input class="acct-input" type="password" name="password" autocomplete="new-password" minlength="10" maxlength="200" required aria-describedby="pw-hint pw-msg"></div>
      <span class="fld-help" id="pw-hint">At least 10 characters. Use one you don't use anywhere else.</span></label>`
    }
    <div class="usr-actions">
      <button class="btn ${view === "remove" ? "danger" : "primary"}" type="submit">${view === "remove" ? "Remove password" : "Save password"}</button>
      <button class="btn" type="button" data-cancel>Cancel</button>
      <button class="link" type="button" data-show aria-pressed="false">Show password</button>
    </div>
    <span class="acct-msg" id="pw-msg" role="status" aria-live="polite"></span>
  </form>`;
  box.innerHTML = `<div class="acct-pw">
    <h3>Password</h3>
    <p class="muted acct-pw-status">${status}</p>
    ${notice ? `<p class="acct-msg" role="status">${esc(notice)}</p>` : ""}
    ${view === "idle" ? `<div class="acct-pw-actions">${actions}</div>` : form}
  </div>`;

  for (const b of box.querySelectorAll<HTMLButtonElement>("[data-view]")) b.addEventListener("click", () => draw(box, email, acct, b.dataset.view as View, ""));
  const f = box.querySelector<HTMLFormElement>("form");
  if (!f) return;
  f.querySelector("[data-cancel]")!.addEventListener("click", () => draw(box, email, acct, "idle", ""));
  const show = f.querySelector<HTMLButtonElement>("[data-show]")!;
  show.addEventListener("click", () => {
    const on = show.getAttribute("aria-pressed") !== "true";
    for (const i of f.querySelectorAll<HTMLInputElement>(".pw-wrap input")) i.type = on ? "text" : "password";
    show.setAttribute("aria-pressed", String(on));
    show.textContent = on ? "Hide password" : "Show password";
  });
  f.querySelector<HTMLInputElement>(".pw-wrap input")?.focus();
  f.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = f.querySelector<HTMLElement>("#pw-msg")!;
    const fail = (code: string, field?: string) => {
      msg.textContent = ERRORS[code] ?? `Something went wrong (${code}).`;
      msg.className = "acct-msg error";
      msg.setAttribute("role", "alert");
      for (const i of f.querySelectorAll<HTMLInputElement>(".pw-wrap input")) i.removeAttribute("aria-invalid");
      const input = field ? f.querySelector<HTMLInputElement>(`input[name="${field}"]`) : null;
      if (input) {
        input.setAttribute("aria-invalid", "true");
        input.focus();
      }
    };
    const data = new FormData(f);
    const cur = String(data.get("current") ?? "");
    const pw = String(data.get("password") ?? "");
    if (needCurrent && !cur) return fail("current_required", "current");
    if (view !== "remove" && [...pw].length < 10) return fail("too_short", "password");
    const btn = f.querySelector<HTMLButtonElement>("button[type=submit]")!;
    btn.disabled = true;
    try {
      const out =
        view === "remove"
          ? await call<Account>("DELETE", "account/password", needCurrent ? { current: cur } : {})
          : await call<Account>("PUT", "account/password", { password: pw, ...(needCurrent ? { current: cur } : {}) });
      const next: Account = { available: true, password: out.password, password_set_at: out.password_set_at, needs_current: out.needs_current };
      const done =
        view === "remove" ? "Password removed. You'll sign in with an email link." : view === "set" ? "Password saved. You can now sign in with it." : "Password changed.";
      draw(box, email, next, "idle", `${done} Any other devices were signed out, and we emailed you about the change.`);
    } catch (err) {
      const code = (err as Error).message;
      fail(code, code === "wrong_password" || code === "current_required" ? "current" : code === "too_many_attempts" ? undefined : "password");
      btn.disabled = false;
    }
  });
}
